import * as focusTrap from 'focus-trap';
import ProductItem from '../components/product/ProductItem';
import ShowcaseDrawerFooter from './ShowcaseDrawerFooter';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  toggleDrawer: '[data-toggle-showcase-drawer]',
  showcaseDrawer: '[data-showcase-drawer]',
  showcaseDrawerContent: '[data-showcase-drawer-content]',
  backdrop: '[data-showcase-drawer-backdrop]',
  close: '[data-showcase-drawer-close]',
  productItem: '[data-product-item]',
};

let activeDrawer = null;
let focusTrapInstance = null;
const drawerFooters = new Map();

function initProductItems(container) {
  container.querySelectorAll(selectors.productItem).forEach(item => {
    new ProductItem(item);
  });
}

function closeDrawer(drawer) {
  drawer.classList.remove('is-open');

  if (activeDrawer !== drawer) {
    return;
  }

  activeDrawer = null;
  enablePageScroll();
  focusTrapInstance?.deactivate();
}

// Drawers live as direct children of the body, or of the dialog they were
// rendered in: a modal <dialog> makes everything outside it inert.
function getMountRoot(el) {
  return el.closest('dialog') || document.body;
}

function findDrawer(root, drawerId) {
  return root.querySelector(`:scope > #${CSS.escape(drawerId)}`);
}

function openDrawer(drawerId, toggle) {
  const drawer =
    findDrawer(getMountRoot(toggle), drawerId) ||
    document.getElementById(drawerId);
  if (!drawer) {
    return;
  }

  document
    .querySelectorAll(`${selectors.showcaseDrawer}.is-open`)
    .forEach(openDrawerEl => {
      if (openDrawerEl !== drawer) {
        closeDrawer(openDrawerEl);
      }
    });

  drawer.classList.add('is-open');
  activeDrawer = drawer;
  disablePageScroll();

  focusTrapInstance?.deactivate();
  focusTrapInstance = focusTrap.createFocusTrap(drawer, {
    clickOutsideDeactivates: true,
    onDeactivate: () => closeDrawer(drawer),
  });
  focusTrapInstance.activate();
  drawerFooters.get(drawerId)?.update();
}

function attachDrawerListeners(drawer) {
  drawer.querySelector(selectors.backdrop)?.addEventListener('click', () => {
    closeDrawer(drawer);
  });

  drawer.querySelectorAll(selectors.close).forEach(closeButton => {
    closeButton.addEventListener('click', () => {
      closeDrawer(drawer);
    });
  });
}

function removeDrawer(drawer) {
  if (!drawer?.matches?.(selectors.showcaseDrawer)) {
    return;
  }

  closeDrawer(drawer);

  if (drawer.id) {
    drawerFooters.delete(drawer.id);
  }

  drawer.remove();
}

function mountDrawerToBody(drawer) {
  if (drawer.dataset.showcaseDrawerMounted === 'true') {
    return;
  }

  const root = getMountRoot(drawer);
  const existing = drawer.id && findDrawer(root, drawer.id);
  if (existing && existing !== drawer) {
    removeDrawer(existing);
  }

  if (drawer.parentElement !== root) {
    root.appendChild(drawer);
  }

  attachDrawerListeners(drawer);
  drawer.dataset.showcaseDrawerMounted = 'true';

  if (drawer.id && !drawerFooters.has(drawer.id)) {
    drawerFooters.set(
      drawer.id,
      new ShowcaseDrawerFooter(drawer, {
        onAddSuccess: () => closeDrawer(drawer),
      }),
    );
  }
}

/**
 * Removes PDP shop-the-look drawers left mounted after async product swaps.
 * Only touches the mount root `scope` belongs to, so swapping the quickshop
 * product leaves the PDP's own drawer alone (and vice versa).
 * @param {HTMLElement} [scope=document.body] - Element inside the product being swapped
 */
export function cleanupPdpShowcaseDrawers(scope = document.body) {
  getMountRoot(scope)
    .querySelectorAll(
      ':scope > [id^="showcase-drawer-pdp-"][data-showcase-drawer]',
    )
    .forEach(removeDrawer);
}

/**
 * Initializes showcase drawers and product lists across the page.
 * @param {HTMLElement} [container=document] - Optional container to limit initialization scope
 */
export default function initShowcase(container = document) {
  container
    .querySelectorAll(selectors.showcaseDrawerContent)
    .forEach(showcase => {
      initProductItems(showcase);
    });

  container
    .querySelectorAll(selectors.showcaseDrawer)
    .forEach(mountDrawerToBody);

  if (container !== document) {
    return;
  }

  document.addEventListener('click', event => {
    const toggle = event.target.closest(selectors.toggleDrawer);
    if (!toggle) {
      return;
    }

    const drawerId = toggle.getAttribute('data-toggle-showcase-drawer');
    if (!drawerId) {
      return;
    }

    event.preventDefault();
    openDrawer(drawerId, toggle);
  });
}
